import { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageHeader } from '@/components/PageHeader';
import { TestConnectionForm } from '@/components/collector/TestConnectionForm';
import { ProgressAnimation } from '@/components/collector/ProgressAnimation';
import { AccountsResultsCard } from '@/components/collector/AccountsResultsCard';
import { SubscriptionModal } from '@/components/collector';
import { useToast } from '@/hooks/use-toast';
import { testPortalBot, getTestPortalInvoice } from '@/services/api/collector';

interface CollectorSupplier {
  id: string | number;
  name: string;
  shortName?: string;
  logoUrl?: string;
}

interface Account {
  serialNo: number;
  accountNumber: string;
  accountAssociated: string;
  latestBillUrl: string | null;
  has12Months: boolean;
}

export default function CollectorSandbox() {
  const { toast } = useToast();
  const [jobId, setJobId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [lastTested, setLastTested] = useState<Date | undefined>(undefined);
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>(undefined);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [testStatus, setTestStatus] = useState<'Success' | 'Failed' | 'Processing'>('Success');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [rateLimitUntil, setRateLimitUntil] = useState<Date | null>(null);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [isPending, setIsPending] = useState(false);
  const [testCredentials, setTestCredentials] = useState<{
    username: string;
    supplierName: string;
  } | null>(null);


  const isTesting = !!jobId || isPending;

  const handleTestConnection = (data: {
    supplierId: string;
    supplierName: string;
    supplierShortName?: string;
    username: string;
    password: string;
  }) => {
    // Check rate limiting
    if (rateLimitUntil && new Date() < rateLimitUntil) {
      const remainingMinutes = Math.ceil((rateLimitUntil.getTime() - new Date().getTime()) / (1000 * 60));
      toast({
        title: 'Rate limit exceeded',
        description: `Please wait ${remainingMinutes} more minutes before trying again.`,
        variant: 'destructive',
      });
      return;
    }

    // Store test credentials for display
    setTestCredentials({
      username: data.username,
      supplierName: data.supplierName,
    });

    // Start animation immediately
    const tempJobId = `temp-${Date.now()}`;
    setJobId(tempJobId);
    setTestStatus('Processing');
    
    // Simulate progress for better UX during long waits
    setSimulatedProgress(0);
    const progressInterval = setInterval(() => {
      setSimulatedProgress(prev => {
        const increment = Math.random() * 3; // Slower, more realistic progress
        const newProgress = prev + increment;
        return newProgress > 85 ? 85 : newProgress; // Don't go to 100% until API responds
      });
    }, 1500); // Faster updates for more responsive feel

    // Safety timeout to stop animation after 3 minutes
    const safetyTimeout = setTimeout(() => {
      clearInterval(progressInterval);
      setSimulatedProgress(0);
      setJobId(null);
      setTestStatus('Failed');
      toast({
        title: 'Connection timeout',
        description: 'The portal test timed out after 3 minutes. Please try again.',
        variant: 'destructive',
      });
    }, 180000); // 3 minutes
    
    setIsPending(true);
    const payload = {
      supplierId: data.supplierId,
      newPortalName: data.supplierShortName || (data.supplierName ? data.supplierName.replace(/\s+/g, '') : undefined),
      username: data.username,
      password: data.password,
    };

    testPortalBot(payload)
      .then((response) => {
        clearInterval(progressInterval); // Clean up progress simulation
        clearTimeout(safetyTimeout); // Clean up safety timeout
        console.log('[Portal Bot] Success response:', response);
        
        // Determine auth result from hasCredentialsCorrect ('yes'/'no' or boolean)
        const hasCorrect = (() => {
          const v = (response as any)?.hasCredentialsCorrect;
          if (typeof v === 'string') return v.toLowerCase() === 'yes' || v.toLowerCase() === 'true';
          if (typeof v === 'boolean') return v;
          return false; // default to false if not provided
        })();

        // Normalize accounts (can be strings or objects) and attach only the first file path for download
        const rawAccounts: any[] = Array.isArray((response as any)?.accounts) ? (response as any).accounts : [];
        const filePaths: string[] = Array.isArray((response as any)?.filePaths) ? (response as any).filePaths : [];

        const transformedAccounts: Account[] = rawAccounts.map((acc: any, index: number) => {
          const accountNumber = typeof acc === 'string' || typeof acc === 'number'
            ? String(acc)
            : (acc?.account_number || acc?.accountNumber || `Account ${index + 1}`);

          // Enable download only for the first account; use filePaths[0] when present
          const latestBillUrl = index === 0
            ? (filePaths?.[0] || acc?.latest_bill_url || acc?.latestBillUrl || null)
            : null;

          return {
            serialNo: index + 1,
            accountNumber,
            accountAssociated: (acc?.account_associated || acc?.accountAssociated || 'N/A'),
            latestBillUrl,
            has12Months: Boolean(acc?.twelve_months_available || acc?.has12Months || false),
          } as Account;
        });

        // Apply success/failed based on hasCredentialsCorrect
        setAccounts(hasCorrect ? transformedAccounts : []);
        setLastTested(new Date());
        setTestStatus(hasCorrect ? 'Success' : 'Failed');
        setJobId(null); // Stop animation
        setSimulatedProgress(100); // Complete the progress
        
        // Reset failed attempts on success
        if (hasCorrect) {
          setFailedAttempts(0);
          setRateLimitUntil(null);
          setIsPending(false);
          toast({
            title: 'Connection test completed',
            description: `Found ${transformedAccounts.length} account${transformedAccounts.length !== 1 ? 's' : ''}`,
          });
        } else {
          setIsPending(false);
          toast({
            title: 'Login failed',
            description: 'Failed to log in to the portal. Please verify your credentials and try again.',
            variant: 'destructive',
          });
        }
      })
      .catch((error: any) => {
        clearInterval(progressInterval); // Clean up progress simulation
        clearTimeout(safetyTimeout); // Clean up safety timeout
        console.error('[Portal Bot] Error:', error);
        
        // Immediately stop animation and reset state
        setTestStatus('Failed');
        setJobId(null); // Stop animation
        setSimulatedProgress(0); // Reset progress
        setIsPending(false);
        
        // Check if it's an authentication error for faster feedback
        const msg = error?.message || String(error);
        const isAuthError = msg?.includes('Invalid credentials') || 
                           msg?.includes('Access denied') ||
                           msg?.includes('Portal not found');
        
        if (isAuthError) {
          console.log('[Portal Bot] Authentication error detected, stopping animation immediately');
        }
        
        // Handle rate limiting
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);
        
        if (newFailedAttempts >= 5) {
          const rateLimitTime = new Date(Date.now() + 5 * 60 * 1000); // 5 minutes from now
          setRateLimitUntil(rateLimitTime);
          
          toast({
            title: 'Rate limit exceeded',
            description: 'Too many failed attempts. Please wait 5 minutes before trying again.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Connection test failed',
            description: msg || 'Please check your credentials and try again.',
            variant: 'destructive',
          });
        }
      });
  };

  const handleCancelJob = () => {
    if (jobId) {
      // Immediately stop animation and reset state
      setJobId(null);
      setTestStatus('Failed');
      setSimulatedProgress(0);
      
      // Cancel the API call if it's still pending
      if (isPending) {
        console.log('Cancelling portal bot test...');
        // We aren't using an AbortController here; the UI state is reset immediately.
      }
      
      toast({
        title: 'Test cancelled',
        description: 'The portal test has been cancelled.',
      });
    }
  };

  const handleDownloadLatest = async (filePath: string) => {
    if (!filePath) return;
    console.log('[Download] Requesting invoice for:', filePath);

    try {
      const blob = await getTestPortalInvoice(filePath);
      // Try to infer filename from path
      const filename = (filePath.split(/[\\/]/).pop() || `latest-bill-${Date.now()}.pdf`).replace(/\?.*$/, '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `latest-bill-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: `Downloading ${filename}`,
      });
    } catch (error) {
      console.error('[Download] Error downloading bill:', error);
      toast({
        title: 'Download failed',
        description: 'Unable to download the bill. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handle12MonthsClick = (account: Account) => {
    console.log('[12 Months] Account:', account.accountNumber, 'Has 12 months:', account.has12Months);
    
    if (account.has12Months) {
      // TODO: Implement 12 months download logic
      toast({
        title: '12 Months Data',
        description: `Downloading 12 months of data for account ${account.accountNumber}`,
      });
    } else {
      // Show subscription modal
      setShowSubscriptionModal(true);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
        <PageHeader
          title="Collector Sandbox"
          description="Test supplier portal credentials with real-time progress tracking"
        />

        <Alert variant="default" className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800">
            <strong>🚀 Portal Collection Bot Testing:</strong> This page showcases our plug-and-play portal collection capabilities. These bots are configured to demonstrate our advanced automation technology. No credentials are stored permanently.
          </AlertDescription>
        </Alert>

        {/* Suppliers preview removed per requirement: suppliers should show only within the dropdown search */}


        {/* Success Banner - Show when test is successful */}
        {accounts.length > 0 && testStatus === 'Success' && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-6 shadow-sm">
            <div className="flex items-center gap-3 text-green-800">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎉</span>
                <span className="font-semibold text-lg">Congratulations!</span>
              </div>
            </div>
            <p className="text-green-700 mt-2 ml-6">You have successfully logged into your account using our plug-and-play portal bot. Below is the list of accounts associated with your profile:</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-6">
          <div className="order-2 lg:order-1">
            {isTesting ? (
              <ProgressAnimation
                percent={Math.floor(simulatedProgress)}
                stage={simulatedProgress < 15 ? 'connecting' : simulatedProgress < 35 ? 'authenticating' : simulatedProgress < 55 ? 'accessing_accounts' : simulatedProgress < 75 ? 'retrieving_bills' : 'parsing'}
                onCancel={handleCancelJob}
              />
            ) : (
              <AccountsResultsCard
                accounts={accounts}
                lastTested={lastTested}
                testStatus={testStatus}
                onDownloadLatest={handleDownloadLatest}
                on12MonthsClick={handle12MonthsClick}
                userId={testCredentials?.username || ''}
                portalName={testCredentials?.supplierName || ''}
                accountAssociated={accounts.length}
              />
            )}
          </div>

          <div className="order-1 lg:order-2">
            <TestConnectionForm
              onSubmit={handleTestConnection}
              isLoading={isPending || isTesting || false}
              remainingAttempts={remainingAttempts}
              isRateLimited={!!rateLimitUntil && new Date() < rateLimitUntil}
              rateLimitMessage={rateLimitUntil && new Date() < rateLimitUntil 
                ? `Rate limit active. Please wait ${Math.ceil((rateLimitUntil.getTime() - new Date().getTime()) / (1000 * 60))} more minutes.`
                : undefined
              }
            />
          </div>
        </div>

        <SubscriptionModal
          open={showSubscriptionModal}
          onOpenChange={setShowSubscriptionModal}
        />
      </div>
  );
}
